import * as THREE from "three";

export class ProjectileManager {
  constructor(scene, blockManager) {
    this.scene = scene;
    this.blockManager = blockManager;
    this.spawners = [];
    this.projectiles = [];

    // Dimensions
    this.spawnerSize = 0.5; // 50px at 1000px effective height
    this.projectileSize = 0.1; // 10px at 1000px effective height

    // Timing
    this.shootInterval = 1200; // milliseconds between shots (reduced from 2000ms)
    this.nextGlobalShootTime = Date.now(); // Global timer for all spawners to shoot at once

    // Projectile speed in world units per 60fps-frame. Scaled by the sim step in
    // update() so travel speed is frame-rate independent (a fixed per-frame step
    // made bullets ~2.4× faster on a 144Hz display than at 60Hz).
    this.projectileSpeed = 0.07;

    // Vertical despawn band for up/down-firing projectiles. The visible play
    // area runs roughly worldY -5..5 (baseGroundY + sceneHeight*cellSize); a
    // couple of cells of margin past that means a vertical projectile is cleaned
    // up just after it leaves the screen instead of flying forever. (Horizontal
    // projectiles are still cleaned up behind the camera's left edge below.)
    this.topCleanupY = 7;
    this.bottomCleanupY = -7;

    // Store creation time to handle first update properly
    this.creationTime = performance.now();
    this.lastUpdateTime = this.creationTime;

    // Particle system for impact effects
    this.particleSystem = null;

    // Shared geometries and materials to reduce memory usage
    this.particleGeometry = new THREE.BoxGeometry(0.05, 0.05, 0.05);
    this.particleMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000,
    });

    this.projectileGeometry = new THREE.BoxGeometry(
      this.projectileSize,
      this.projectileSize,
      0.05
    );

    this.projectileMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000,
      roughness: 0.7,
      metalness: 0.2,
      emissive: 0x222222,
      emissiveIntensity: 0.5,
    });

    this.spawnerMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000,
    });

    // Shared spawner part geometries. Every spawner is identical: a top and
    // bottom bar (same dims) plus a right side bar. Disposed once in dispose().
    const spawnerBarHeight = 0.2;
    const rightSideWidth = 0.2;
    this.spawnerBarGeometry = new THREE.BoxGeometry(
      this.spawnerSize,
      spawnerBarHeight,
      0.05
    );
    this.spawnerSideGeometry = new THREE.BoxGeometry(
      rightSideWidth,
      this.spawnerSize - spawnerBarHeight * 2,
      0.05
    );

    this.setupParticleSystem();
  }

  setupParticleSystem() {
    // Create a simple particle system for impact effects
    const particleCount = 40; // Increased for a more dense effect

    this.particleSystem = {
      particles: [],
      create: (x, y) => {
        for (let i = 0; i < particleCount; i++) {
          const particle = new THREE.Mesh(
            this.particleGeometry,
            this.particleMaterial
          );
          particle.position.set(x, y, 0);

          // Calculate a random angle for circular distribution
          const angle = Math.random() * Math.PI * 2; // Full 360 degrees in radians
          const speed = 0.05 + Math.random() * 0.1; // Reduced speed for more localized effect

          particle.userData = {
            velocity: {
              // Use trigonometry to create circular distribution
              x: Math.cos(angle) * speed,
              y: Math.sin(angle) * speed,
            },
            lifetime: 0,
            maxLifetime: 5 + Math.random() * 3, // 5-8 seconds
            active: true,
          };
          this.scene.add(particle);
          this.particleSystem.particles.push(particle);
        }
      },
      update: (deltaTime, cameraPosition, visibleRange) => {
        // Calculate the camera's left edge with a small buffer
        const cameraLeftEdge = cameraPosition - visibleRange / 2 - 2; // 2 unit buffer

        // Apply gravity and movement to particles
        for (let i = this.particleSystem.particles.length - 1; i >= 0; i--) {
          const particle = this.particleSystem.particles[i];
          if (!particle.userData.active) continue;

          // Update lifetime
          particle.userData.lifetime += deltaTime;

          // Check if particle is behind the camera's left edge
          if (particle.position.x < cameraLeftEdge) {
            this.disposeParticle(particle, i);
            continue;
          }

          if (particle.userData.lifetime >= particle.userData.maxLifetime) {
            this.disposeParticle(particle, i);
            continue;
          }

          // Fade out particle based on lifetime (start fading at 75% of lifetime)
          const fadeThreshold = particle.userData.maxLifetime * 0.75;
          if (particle.userData.lifetime > fadeThreshold) {
            const fadeRatio =
              1 -
              (particle.userData.lifetime - fadeThreshold) /
                (particle.userData.maxLifetime - fadeThreshold);
            const scale = Math.max(0.3, fadeRatio);
            particle.scale.set(scale, scale, scale);
          }

          // Apply weaker gravity for a gentler, longer-lasting effect
          particle.userData.velocity.y -= 0.003 * deltaTime;

          // Gradually slow down particles over time (air resistance)
          const drag = 0.99;
          particle.userData.velocity.x *= drag;
          particle.userData.velocity.y *= drag;

          // Move particle
          particle.position.x += particle.userData.velocity.x * deltaTime;
          particle.position.y += particle.userData.velocity.y * deltaTime;

          // Check for collision with ground
          if (particle.position.y < -5) {
            particle.position.y = -5;
            particle.userData.velocity.y *= -0.5; // Bounce with reduced energy
            particle.userData.velocity.x *= 0.9; // Add friction when hitting ground
          }
        }
      },
    };
  }

  disposeParticle(particle, index) {
    // Remove from scene and array
    this.scene.remove(particle);
    // No need to dispose of geometry/material as they are shared
    if (index !== undefined) {
      this.particleSystem.particles.splice(index, 1);
    }
    particle.userData.active = false;
  }

  disposeProjectile(projectile, index) {
    if (!projectile) return;

    // Remove from scene
    this.scene.remove(projectile.mesh);

    // No need to dispose geometry/material as they are shared

    // Remove from array if index provided
    if (index !== undefined) {
      this.projectiles.splice(index, 1);
    }
  }

  disposeSpawner(spawner) {
    if (!spawner) return;

    // Unregister from block manager
    if (spawner.blockData) {
      this.blockManager.unregisterExternalBlock(spawner.blockData);
    }

    // Remove from scene. Geometry and material are shared across all spawners,
    // so they are NOT disposed here — only once in dispose() at teardown.
    if (spawner.mesh) {
      this.scene.remove(spawner.mesh);
    }

    // Clear references
    spawner.mesh = null;
    spawner.bounds = null;
    spawner.blockData = null;
  }

  reset() {
    // Remove all spawners and projectiles from scene
    this.spawners.forEach((spawner) => {
      this.disposeSpawner(spawner);
    });

    this.projectiles.forEach((projectile) => {
      this.disposeProjectile(projectile);
    });

    // Clear arrays
    this.spawners = [];
    this.projectiles = [];

    // Reset timing system to ensure consistency across restarts
    this.creationTime = performance.now();
    this.lastUpdateTime = this.creationTime;
    this.nextGlobalShootTime = Date.now(); // Reset global shoot timer

    // Clean up particle system - more aggressively remove particles
    if (this.particleSystem) {
      // Force disposal of all particles, even if they're still active
      for (let i = this.particleSystem.particles.length - 1; i >= 0; i--) {
        this.disposeParticle(this.particleSystem.particles[i], i);
      }

      // Ensure array is cleared
      this.particleSystem.particles = [];
    }
  }

  // Create a projectile spawner. `direction` selects which way it fires:
  //  - "left" (default): the original horizontal turret — fires toward the
  //    oncoming player; needs a backstop block to its left (template char `p`).
  //  - "down": a vertical turret — fires straight down (template char `P`),
  //    typically mounted on a ceiling/overhang so the player times passing
  //    underneath. ("up" is also supported for symmetry.) Vertical turrets need
  //    no horizontal backstop; their rounds are despawned past the play area.
  // The box itself is identical; the group is rotated 90° so its open face
  // points the firing way, and the barrel opening sits on that edge of the cell.
  createSpawner(x, y, direction = "left") {
    // Parent object to hold all the spawner parts
    const spawnerGroup = new THREE.Group();
    spawnerGroup.position.set(
      x + this.spawnerSize / 2,
      y + this.spawnerSize / 2,
      0
    );

    // Rotate the box so the (authored left-facing) opening points the firing
    // way. The group is centered on the cell and the collision bounds are a
    // square, so the rotation changes only the visual orientation, not the AABB.
    if (direction === "up") {
      spawnerGroup.rotation.z = -Math.PI / 2;
    } else if (direction === "down") {
      spawnerGroup.rotation.z = Math.PI / 2;
    }

    // Dimensions are in UNITS where 0.5 units = 50px at 1000px effective height
    // This means 0.1 units = 10px, 0.2 units = 20px, 0.3 units = 30px
    const spawnerWidth = this.spawnerSize; // 0.5 units = 50px
    const spawnerHeight = this.spawnerSize; // 0.5 units = 50px
    const topHeight = 0.2; // 20px at 1000px effective height
    const bottomHeight = 0.2; // 20px at 1000px effective height
    const rightSideWidth = 0.2; // 20px at 1000px effective height

    // 1. Top part (full width, 20px height) — shared bar geometry/material
    const topMesh = new THREE.Mesh(this.spawnerBarGeometry, this.spawnerMaterial);
    topMesh.castShadow = true;
    topMesh.receiveShadow = false;
    topMesh.position.set(0, spawnerHeight / 2 - topHeight / 2, 0);
    spawnerGroup.add(topMesh);

    // 2. Bottom part (full width, 20px height) — same shared bar geometry
    const bottomMesh = new THREE.Mesh(
      this.spawnerBarGeometry,
      this.spawnerMaterial
    );
    bottomMesh.castShadow = true;
    bottomMesh.receiveShadow = false;
    bottomMesh.position.set(0, -spawnerHeight / 2 + bottomHeight / 2, 0);
    spawnerGroup.add(bottomMesh);

    // 3. Right side part (20px width, full height minus top/bottom) — shared
    const rightSideMesh = new THREE.Mesh(
      this.spawnerSideGeometry,
      this.spawnerMaterial
    );
    rightSideMesh.castShadow = true;
    rightSideMesh.receiveShadow = false;

    // Position on the right side, in the vertical middle between top and bottom parts
    rightSideMesh.position.set(spawnerWidth / 2 - rightSideWidth / 2, 0, 0);
    spawnerGroup.add(rightSideMesh);

    // Add the whole group to the scene
    this.scene.add(spawnerGroup);

    // Define bounds for collision detection
    const bounds = {
      left: x,
      right: x + this.spawnerSize,
      top: y + this.spawnerSize,
      bottom: y,
    };

    // Register with the block manager for player collision detection
    const blockData = this.blockManager.registerExternalBlock(
      x,
      y,
      this.spawnerSize,
      this.spawnerSize
    );

    // Barrel opening + the unit direction (dx, dy) projectiles travel. A
    // horizontal turret fires left from the inner edge of its right wall
    // (dx=-1); a vertical turret fires from the top/bottom edge of the cell
    // (dy=±1) through the horizontal center.
    let barrelX;
    let barrelY;
    let dx;
    let dy;
    if (direction === "up") {
      barrelX = x + spawnerWidth / 2; // horizontal center
      barrelY = y + spawnerHeight; // top edge of the cell
      dx = 0;
      dy = 1;
    } else if (direction === "down") {
      barrelX = x + spawnerWidth / 2; // horizontal center
      barrelY = y; // bottom edge of the cell
      dx = 0;
      dy = -1;
    } else {
      barrelX = x + (spawnerWidth - rightSideWidth); // inner edge of the right part
      barrelY = y + spawnerHeight / 2; // vertical center
      dx = -1;
      dy = 0;
    }

    // Mark this as a projectile spawner for collision detection
    spawnerGroup.userData = {
      ...spawnerGroup.userData,
      bounds,
      isProjectileSpawner: true,
      direction,
      openingPosition: {
        x: barrelX,
        y: barrelY,
      },
    };

    // Add to spawners array for tracking
    this.spawners.push({
      mesh: spawnerGroup,
      bounds: bounds,
      blockData: blockData, // Store the block data for cleanup
      dx, // unit firing direction, applied per-projectile in update()
      dy,
    });

    return {
      mesh: spawnerGroup,
      bounds: bounds,
    };
  }

  // Create a projectile travelling in unit direction (dx, dy). Defaults to the
  // original leftward bullet so existing callers are unchanged.
  createProjectile(x, y, dx = -1, dy = 0) {
    // Create a projectile using shared geometry and material
    const mesh = new THREE.Mesh(
      this.projectileGeometry,
      this.projectileMaterial
    );
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    mesh.position.set(x, y, 0);

    this.scene.add(mesh);

    // Define bounds for collision detection
    const bounds = {
      left: x - this.projectileSize / 2,
      right: x + this.projectileSize / 2,
      top: y + this.projectileSize / 2,
      bottom: y - this.projectileSize / 2,
    };

    // Mark as a projectile for collision detection
    mesh.userData = {
      bounds,
      isProjectile: true,
    };

    // Add to projectiles array for tracking
    this.projectiles.push({
      mesh: mesh,
      bounds: bounds,
      dx, // unit travel direction (set by the spawner)
      dy,
    });

    return {
      mesh: mesh,
      bounds: bounds,
    };
  }

  update(normalizedStep, playerDistance, cameraPosition, visibleRange) {
    // Use our own timing to ensure consistency through restarts
    const currentRealTime = performance.now();
    // Calculate our own deltaTime to be independent of the Game's timing system
    const independentDeltaTime = currentRealTime - this.lastUpdateTime;
    this.lastUpdateTime = currentRealTime;

    const currentTime = Date.now();

    // Check global shoot timer
    if (currentTime >= this.nextGlobalShootTime && this.spawners.length > 0) {
      // Create projectiles from all spawners at once
      this.spawners.forEach((spawner) => {
        // Only create projectiles for spawners that are either:
        // 1. Within the visible range OR
        // 2. Ahead of the camera (to the right) but within a reasonable spawn distance
        const spawnerX = spawner.mesh.position.x;
        const isAheadOfCamera = spawnerX > cameraPosition + visibleRange / 2;
        const isWithinVisibleRange =
          Math.abs(spawnerX - cameraPosition) <= visibleRange;
        const isWithinSpawnDistance =
          spawnerX <= cameraPosition + visibleRange + 15; // Allow spawners up to 15 units ahead

        if (
          isWithinVisibleRange ||
          (isAheadOfCamera && isWithinSpawnDistance)
        ) {
          // Create a new projectile at the spawner's opening, travelling in the
          // spawner's firing direction (left for horizontal, up for vertical).
          const openingPos = spawner.mesh.userData.openingPosition;
          this.createProjectile(
            openingPos.x,
            openingPos.y,
            spawner.dx,
            spawner.dy
          );
        }
      });

      // Update next global shoot time
      this.nextGlobalShootTime = currentTime + this.shootInterval;
    }

    // Get blocks from the block manager
    const blocks = this.blockManager.getBlocks();

    // Distance to travel this tick: constant world-speed scaled by the normalized
    // sim step, so bullets move the same speed regardless of frame rate. This also
    // inherits the loop's MAX_STEP clamp, so a bullet can't tunnel past the player.
    const stepDistance = this.projectileSpeed * normalizedStep;

    // Calculate the camera's left edge with a small buffer
    const cameraLeftEdge = cameraPosition - visibleRange / 2 - 2; // 2 unit buffer

    // Update projectiles with fixed speed
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];

      // Move the projectile along its unit direction by the per-tick step
      // distance computed above (left for horizontal turrets, up for vertical).
      // Legacy projectiles without a stored direction default to leftward.
      const dx = projectile.dx ?? -1;
      const dy = projectile.dy ?? 0;
      projectile.mesh.position.x += dx * stepDistance;
      projectile.mesh.position.y += dy * stepDistance;

      // Update collision bounds
      projectile.bounds = {
        left: projectile.mesh.position.x - this.projectileSize / 2,
        right: projectile.mesh.position.x + this.projectileSize / 2,
        top: projectile.mesh.position.y + this.projectileSize / 2,
        bottom: projectile.mesh.position.y - this.projectileSize / 2,
      };

      // Update mesh userData bounds to match
      projectile.mesh.userData.bounds = projectile.bounds;

      // Despawn once off-screen: behind the camera's left edge (horizontal, and
      // also catches a vertical one that has scrolled away), or past the top /
      // bottom of the play area (vertical). Either way the mesh is freed so a
      // long run can't accumulate stray projectiles.
      if (
        projectile.mesh.position.x < cameraLeftEdge ||
        projectile.mesh.position.y > this.topCleanupY ||
        projectile.mesh.position.y < this.bottomCleanupY
      ) {
        this.disposeProjectile(projectile, i);
        continue;
      }

      // Check for collision with blocks
      let collided = false;

      if (blocks && Array.isArray(blocks)) {
        for (const block of blocks) {
          // Skip external blocks (spawners) to prevent self-collision
          if (block && block.isExternal) {
            continue;
          }

          if (
            block &&
            block.bounds &&
            this.checkCollision(projectile.bounds, block.bounds)
          ) {
            // Create particle effect at collision point
            this.particleSystem.create(
              projectile.mesh.position.x,
              projectile.mesh.position.y
            );

            // Remove this projectile
            this.disposeProjectile(projectile, i);
            collided = true;
            break;
          }
        }
      }
    }

    // Update particle system with our independent time
    if (this.particleSystem) {
      // Using 16.67 as reference time (60fps) to normalize
      // Cap at 100ms to prevent huge jumps, but keep the scale reasonable
      const normalizedDelta =
        (Math.min(independentDeltaTime, 100) / 16.67) * 0.5;
      this.particleSystem.update(normalizedDelta, cameraPosition, visibleRange);
    }

    // Clean up spawners that are behind the camera's left edge
    for (let i = this.spawners.length - 1; i >= 0; i--) {
      const spawner = this.spawners[i];
      if (spawner.mesh.position.x < cameraLeftEdge) {
        this.disposeSpawner(spawner);
        this.spawners.splice(i, 1);
      }
    }
  }

  checkCollision(a, b) {
    // Simple AABB collision detection
    return (
      a.left < b.right &&
      a.right > b.left &&
      a.top > b.bottom &&
      a.bottom < b.top
    );
  }

  // Get spawners for collision detection
  getSpawners() {
    return this.spawners.map((spawner) => spawner.mesh);
  }

  // Get spawner bounds for collision detection
  getSpawnerBounds() {
    return this.spawners.map((spawner) => spawner.bounds);
  }

  // Get projectiles for collision detection
  getProjectiles() {
    return this.projectiles.map((projectile) => projectile.mesh);
  }

  // Get projectile bounds for collision detection
  getProjectileBounds() {
    return this.projectiles.map((projectile) => projectile.bounds);
  }

  // Remove projectiles before a certain X position
  removeProjectilesBeforeX(x) {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      if (this.projectiles[i].mesh.position.x < x) {
        this.disposeProjectile(this.projectiles[i], i);
      }
    }
  }

  // Remove spawners before a certain X position
  removeSpawnersBeforeX(x) {
    for (let i = this.spawners.length - 1; i >= 0; i--) {
      const spawner = this.spawners[i];
      // Check spawner's right edge using bounds
      if (spawner.bounds.right < x) {
        this.disposeSpawner(spawner);
        this.spawners.splice(i, 1);
      }
    }
  }

  // Properly dispose all resources when game is destroyed
  dispose() {
    // Clean up all spawners
    this.spawners.forEach((spawner) => {
      this.disposeSpawner(spawner);
    });

    // Clean up all projectiles
    this.projectiles.forEach((projectile) => {
      this.disposeProjectile(projectile);
    });

    // Clean up all particles
    if (this.particleSystem) {
      this.particleSystem.particles.forEach((particle) => {
        this.disposeParticle(particle);
      });
    }

    // Dispose shared geometries and materials
    this.particleGeometry.dispose();
    this.particleMaterial.dispose();
    this.projectileGeometry.dispose();
    this.projectileMaterial.dispose();
    this.spawnerMaterial.dispose();
    this.spawnerBarGeometry.dispose();
    this.spawnerSideGeometry.dispose();

    // Clear arrays and references
    this.spawners = [];
    this.projectiles = [];
    if (this.particleSystem) {
      this.particleSystem.particles = [];
    }
  }
}
